import {
  createContext,
  type MutableRefObject,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useConsultantAccess } from '../../hooks/consultant/use-consultant-access';
import type { SurveyAuthoringOverlayApi } from '../../pages/consultant/surveys/survey-authoring-overlay';
import type { AssistantQuickAction, SurveyAuthoringTurnResult } from './assistant-panel';
import { AssistantShellFrame } from './assistant-shell-frame';

/**
 * Slot the survey-studio builder page registers its `SurveyAuthoringOverlayApi`
 * into. The shell mounts the panel OUTSIDE the page's
 * `SurveyAuthoringOverlayProvider` (the fullscreen builder shell renders
 * `position: fixed; inset: 0`), so the page hands its API up through this ref
 * (same collector pattern as `ManualDraftOverlayContext` on the manual page):
 * the panel reads it at SEND time, never at render time.
 */
export const SurveyAuthoringOverlayRefContext =
  createContext<MutableRefObject<SurveyAuthoringOverlayApi | null> | null>(null);

/** Reads (nullable) the overlay API the builder page registered. */
export function useSurveyAuthoringOverlayRef(): MutableRefObject<SurveyAuthoringOverlayApi | null> | null {
  return useContext(SurveyAuthoringOverlayRefContext);
}

/**
 * Route shell for the Survey Studio builder page (`/consultant/surveys/:templateId`),
 * twin of `DocGenerationAssistantShell`. Mounted INSIDE the route element (the
 * fullscreen builder page is the child), so the FAB exists only here — never
 * on other consultant pages. The builder shell is fullscreen at
 * `zIndex.drawer + 50`: the FAB sits at +52 (above the shell, above the
 * doc-gen banner at +51) and the panel portals itself at drawer+70, both
 * below MUI dialogs (1300) per Task 13's z-index window.
 *
 * The voice control is disabled on this route BY PROP: `sendVoiceMessage`
 * never forwards `surveyAuthoring` (voice = normal turn on the ACTIVE
 * tenant), so a voice turn from the studio would bill and thread the wrong
 * tenant — CP2 note (g).
 *
 * The whole assistant is mounted ONLY when the consultant AI is on
 * (`consultantAiEnabled` di `GET /consultant/access`: a studio AND the back-office
 * switch): the survey studio itself is open to any Consulente, but the
 * authoring turn bills the AI quota on the studio and fails closed with a 403
 * without it. The flag is read, never re-derived — and while it is still
 * loading nothing is offered, so the FAB never appears only to vanish. The
 * builder page explains the absence in its banner stack.
 */
export function SurveyAuthoringAssistantShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { consultantAiEnabled, isPending: consultantAccessPending } = useConsultantAccess();
  const { templateId } = useParams<{ templateId: string }>();
  const overlayApiRef = useRef<SurveyAuthoringOverlayApi | null>(null);
  const [assistantOpen, setAssistantOpen] = useState(false);

  const quickActions = useMemo<AssistantQuickAction[]>(
    () => [
      {
        id: 'translations',
        label: t('consultant.surveyStudio.assistant.quickTranslations'),
        prompt: t('consultant.surveyStudio.assistant.translationsPrompt'),
      },
      {
        id: 'normative',
        label: t('consultant.surveyStudio.assistant.quickNormativeCheck'),
        prompt: t('consultant.surveyStudio.assistant.normativePrompt'),
      },
    ],
    [t],
  );

  /**
   * Built fresh at each send: `schema` is the dirty builder overlay when the
   * user has unsaved edits, `null` when the persisted DRAFT is the source of
   * truth (contract D4, Task 10 note c). The context is what switches the
   * WHOLE turn — the auto greeting included — to the consultant workspace.
   */
  const getSurveyAuthoring = useCallback(() => {
    if (!templateId) {
      return null;
    }
    return { templateId, schema: overlayApiRef.current?.getDirtySchema() ?? null };
  }, [templateId]);

  const handleSurveyAuthoringResult = useCallback((result: SurveyAuthoringTurnResult) => {
    // The schema in the result is ALREADY persisted server-side by the apply
    // tool (Task 13 note c): apply it straight onto the builder — NEVER
    // invalidate-refetch. markClean documents the post-apply state.
    const api = overlayApiRef.current;
    if (!api) {
      return;
    }
    api.applyRemoteSchema(result.schema);
    api.markClean();
  }, []);

  if (consultantAccessPending || !consultantAiEnabled) {
    return (
      <SurveyAuthoringOverlayRefContext.Provider value={overlayApiRef}>
        {children}
      </SurveyAuthoringOverlayRefContext.Provider>
    );
  }

  return (
    <SurveyAuthoringOverlayRefContext.Provider value={overlayApiRef}>
      {children}
      <AssistantShellFrame
        // Fab above the fullscreen builder shell (drawer+50) and the doc-gen
        // banner (+51), below MUI dialogs.
        fabSx={{ zIndex: (theme) => theme.zIndex.drawer + 52 }}
        getSurveyAuthoring={getSurveyAuthoring}
        onOpenChange={setAssistantOpen}
        onSurveyAuthoringResult={handleSurveyAuthoringResult}
        open={assistantOpen}
        quickActions={quickActions}
        voiceDisabled
      />
    </SurveyAuthoringOverlayRefContext.Provider>
  );
}
